export default class Graph {
  constructor () {
    this.adj = new Map()
    this.incoming = new Map() // nodeId -> # of incoming edges
  }

  addNode (id) {
    if (!this.adj.has(id)) {
      this.adj.set(id, [])
      this.incoming.set(id, 0)
    }
  }

  /**
   * Register a directed edge
   * @param {string} from - source node id
   * @param {string} to - target node id
   * @param {string} edgeId - edge identifier
   * @param {object} opts - { isQC?: boolean, parentId?: string }
   */
  addEdge (from, to, edgeId, isQC = false) {
    if (!this.adj.has(from) || !this.adj.has(to)) {
      throw new Error(`Unknown node in edge ${edgeId}: ${from} → ${to}`)
    }

    // Special case: ignore QC edge going back to parent
    if (isQC) {
      console.log(`Skipping QC edge ${edgeId}: ${from} → ${to}`)
      return
    }

    this.adj.get(from).push({ to, id: edgeId })
    this.incoming.set(to, this.incoming.get(to) + 1)
  }

  removeEdge (edgeId) {
    for (const [u, edges] of this.adj) {
      const before = edges.length
      const filtered = edges.filter(e => e.id !== edgeId)
      if (filtered.length !== before) {
        const removed = edges.filter(e => e.id === edgeId)
        this.adj.set(u, filtered)
        for (const r of removed) {
          this.incoming.set(r.to, this.incoming.get(r.to) - 1)
        }
        break
      }
    }
  }

  removeNode (nodeId) {
    if (!this.adj.has(nodeId)) return

    // 1) remove outgoing edges
    for (const { to } of this.adj.get(nodeId)) {
      this.incoming.set(to, this.incoming.get(to) - 1)
    }
    this.adj.delete(nodeId)
    this.incoming.delete(nodeId)

    // 2) remove incoming edges from others
    for (const [u, edges] of this.adj) {
      this.adj.set(u, edges.filter(e => e.to !== nodeId))
    }
  }

  getAllEdgeIds () {
    const all = []
    for (const edges of this.adj.values()) {
      for (const e of edges) {
        all.push(e.id)
      }
    }
    return [...new Set(all)]
  }

  findStartNodeIds () {
    return [...this.incoming].filter(([_, cnt]) => cnt === 0).map(([id]) => id)
  }

  findEndNodeIds () {
    return [...this.adj].filter(([_, edges]) => edges.length === 0).map(([id]) => id)
  }

  findCyclicEdgeIds () {
    const indexMap = new Map()
    const lowlink = new Map()
    const stack = []
    const onStack = new Set()
    const sccs = []
    let idx = 0

    const strongconnect = (v) => {
      indexMap.set(v, idx)
      lowlink.set(v, idx)
      idx++
      stack.push(v)
      onStack.add(v)

      for (const { to: w } of this.adj.get(v)) {
        if (!indexMap.has(w)) {
          strongconnect(w)
          lowlink.set(v, Math.min(lowlink.get(v), lowlink.get(w)))
        } else if (onStack.has(w)) {
          lowlink.set(v, Math.min(lowlink.get(v), indexMap.get(w)))
        }
      }

      if (lowlink.get(v) === indexMap.get(v)) {
        const scc = []
        let u
        do {
          u = stack.pop()
          onStack.delete(u)
          scc.push(u)
        } while (u !== v)
        sccs.push(scc)
      }
    }

    for (const nodeId of this.adj.keys()) {
      if (!indexMap.has(nodeId)) {
        strongconnect(nodeId)
      }
    }

    const cyclicEdges = new Set()
    for (const scc of sccs) {
      const isCycle = scc.length > 1 || this.adj.get(scc[0]).some(e => e.to === scc[0])
      if (isCycle) {
        for (const u of scc) {
          for (const e of this.adj.get(u)) {
            if (scc.includes(e.to)) {
              cyclicEdges.add(e.id)
            }
          }
        }
      }
    }

    return [...cyclicEdges]
  }
}
